import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from '@emotion/styled'
import dayjs from 'dayjs'

import Text from '../../components/common/Text'
import Comment from '../../components/Comment'
import ImageSlider from '../../components/common/ImageSlider'
import {
  useGetNewsDetail,
  useReadNewsDetail,
  useSentQuizAnswer,
  useVote,
} from '../../services/digital-news/digital-news-query'
import { FONT_LIGHT_GRAY, FONT_BLACK, PRIMARY } from '../../constant/colors'
import Tag from '../../components/common/Tag'
import Image from '../../components/common/Image'
import coinsIcon from '../../assets/images/icon-coins.svg'
import Button from '../../components/common/Button'
import { useScreen } from '../../utils/responsive-helper'
import FooterContent from '../../components/FooterContent'
import { useGetUser } from '../../services/user/user-query'
import { useRouter } from '../../utils/helper'
import ImageInfographicCoinSuccess from '../../assets/images/infographic-coin-success.svg'
import ImageInfographicQuizFalse from '../../assets/images/infographic-quiz-false.svg'
import Modal from '../../components/common/Modal'
import { useTranslation } from 'react-i18next'
import Radio from '../../components/common/Radio'
import Breadcrumb, { ItemProps } from '../../components/common/Breadcrumb'
import Loading from '../../components/common/Loading'

dayjs.locale('th')

const HeaderText = styled(Text)`
  margin-top: 24px;
  margin-bottom: 16px;
`
const BodyLayout = styled.div<{ screen: any }>`
  display: flex;
  flex-direction: ${({ screen }) => (screen.isMobile ? 'column' : 'row')};
`
const CommentLayout = styled.div`
  width: 365px;
  max-width: calc(100vw - 32px);
`
const NewsLayout = styled.div`
  width: 751px;
  margin-right: 24px;
  margin-bottom: 24px;
  max-width: calc(100vw - 32px);
  .image-slider {
    width: 100%;
    max-width: calc(100vw - 32px);
    height: 420px;
    object-fit: contain;
  }
`
const NewsContent = styled.div`
  background: white;
  padding: 24px;
  img {
    max-width: 100%;
  }
`
const UpperContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 26px;
`
const FlexChild = styled.div`
  flex: 1;
`
const IconImage = styled(Image)`
  width: 16px;
  height: 15px;
  margin-left: 16px;
`
const ReadButton = styled(Button)`
  width: 267px;
`
const CoinSuccessImagePanel = styled.div`
  text-align: center;
  margin-bottom: 64px;
`
const ModalButton = styled(Button)`
  margin: 32px;
`
const ModalButtonPanel = styled.div`
  display: flex;
  flex-flow: row;
  justify-content: center;
`
const CenteredText = styled(Text)`
  text-align: center;
`
const ResponseModal = styled(Modal)`
  min-width: calc(40vw);
  max-width: calc(90vw);
`

const ModalContainer = styled.div`
  margin-top: 50px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

const ButtonStyle = styled(Button)`
  width: 100%;
`

const NewsDetail = () => {
  const [radioValue, setRadioValue] = useState('')
  const { data: user } = useGetUser()
  const { query } = useRouter<{ newsId: string }>()
  const { data, isLoading } = useGetNewsDetail({ id: query.newsId, userId: user?.userId })
  const { digitalNews, isInform } = data || {}
  const screen = useScreen()
  const { t } = useTranslation()
  const { isMobile } = screen
  const thaiNewsDate = useMemo(() => {
    return `${dayjs(data?.digitalNews?.createDate).format('DD MMM')} ${
      Number(dayjs(data?.digitalNews?.createDate).format(`YYYY`)) + 543
    }`
  }, [data?.digitalNews?.createDate])
  const [modalVisible, setModalVisible] = useState(false)
  const [isModalSuccess, setIsModalSuccess] = useState(true)
  const [modalVisibleFaild, setModalVisibleFaild] = useState(false)
  const [modalJoin, setModalJoin] = useState(false)

  const responseModalData = useMemo(() => {
    if (isModalSuccess) {
      return {
        image: ImageInfographicCoinSuccess,
        text: !!digitalNews?.rewardCoin
          ? t('newsDetail.modalText')
          : t('newsDetail.modalTextNoCoin'),
        buttonText: t('newsDetail.modalButtonText'),
      }
    } else {
      return {
        image: ImageInfographicQuizFalse,
        text: t('newsDetail.modalTextFail'),
        buttonText: t('newsDetail.modalButtonText'),
      }
    }
  }, [isModalSuccess, digitalNews?.rewardCoin, t])

  const onModalOk = useCallback(() => {
    setModalVisible(false)
  }, [])

  useEffect(() => {
    setRadioValue('')
  }, [])

  useEffect(() => {
    if (data?.digitalNews.type === 'INFORM') {
      setTimeout(function () {
        if (data.isInform === false) {
          onActionClick()
          setModalVisible(true)
        }
      }, 3000)
    }
  }, [data])

  const { mutate: read } = useReadNewsDetail()
  const { mutate: answerQuiz } = useSentQuizAnswer()
  const { mutate: vote } = useVote()

  const canAction = useMemo(() => {
    return !isInform
  }, [isInform])

  const onSuccess = useCallback(
    (response) => {
      if (!response.isCorrect && data?.digitalNews.type === 'QUIZ') {
        setIsModalSuccess(false)
      } else {
        setIsModalSuccess(true)
      }
      setModalVisible(true)
    },
    [data],
  )

  const overideBreadCrumb = useCallback(
    (paths: ItemProps[]) => {
      paths[1] = { ...paths[1], label: digitalNews?.type || '' }
      return paths
    },
    [digitalNews],
  )
  const onActionClick = useCallback(() => {
    const { type } = digitalNews || {}
    if (type === 'QUIZ') {
      digitalNews &&
        answerQuiz(
          {
            userId: user?.userId,
            digitalNewsId: digitalNews.id.toString(),
            subType: digitalNews.subType,
            quizOptionId: radioValue,
          },
          { onSuccess },
        )
    } else if (type === 'VOTE') {
      if (radioValue && digitalNews) {
        digitalNews &&
          vote(
            {
              userId: user?.userId,
              digitalNewsId: digitalNews.id.toString(),
              subType: digitalNews.subType,
              voteOptionId: radioValue,
            },
            { onSuccess },
          )
      } else {
        setModalVisibleFaild(true)
      }
    } else if (type === 'INFORM') {
      digitalNews &&
        read(
          {
            userId: user?.userId,
            digitalNewsId: digitalNews.id.toString(),
            subType: digitalNews.subType,
          },
          { onSuccess },
        )
    } else if (type === 'REGISTER') {
      if (digitalNews) {
        digitalNews &&
          read(
            {
              userId: user?.userId,
              digitalNewsId: digitalNews.id.toString(),
              subType: digitalNews.subType,
            },
            { onSuccess },
          )
        setModalJoin(false)
      } else {
        setModalJoin(true)
      }
    }
  }, [digitalNews, answerQuiz, user?.userId, radioValue, onSuccess, vote, read])

  const imagesForSlider = useMemo(() => {
    return digitalNews?.digitalNewsHeaderImagesList?.map((obj: any) => ({
      imagePath: `${process.env.REACT_APP_API_HOST}${obj.imagePath}`,
    }))
  }, [digitalNews])

  const radioContent = useMemo(() => {
    const { type, digitalNewsQuizList = [], digitalNewsVoteList = [] } = digitalNews || {}
    let options: BaseOptionType[] = []
    if (type === 'QUIZ') {
      options = digitalNewsQuizList.map((obj: { id: number; quizOption: string }) => ({
        label: obj.quizOption,
        value: obj.id,
      }))
    } else if (type === 'VOTE') {
      options = digitalNewsVoteList.map((obj: { id: number; voteOption: string }) => ({
        label: obj.voteOption,
        value: obj.id,
      }))
    }

    return <Radio options={options} value={radioValue} setValue={setRadioValue} />
  }, [digitalNews, radioValue])

  const buttonText = useMemo(() => {
    const { type } = digitalNews || {}
    if (type === 'QUIZ') {
      return t('newsDetail.sentAnswer')
    } else if (type === 'VOTE') {
      return t('newsDetail.vote')
    } else if (type === 'INFORM') {
      return t('newsDetail.readButton')
    } else if (type === 'REGISTER') {
      return t('newsDetail.register')
    }
  }, [digitalNews, t])

  return (
    <Loading loading={isLoading}>
      <Breadcrumb style={{ marginTop: 24 }} overide={overideBreadCrumb} />
      <HeaderText size={32} type="Bold">
        {digitalNews?.title || '-'}
      </HeaderText>
      {digitalNews && (
        <BodyLayout screen={screen}>
          <NewsLayout>
            <ImageSlider
              images={imagesForSlider}
              style={{ maxWidth: 'calc(95vw)' }}
              status="Detail"
            />
            <NewsContent>
              <UpperContent>
                <Tag label={digitalNews.type} type={digitalNews.type} fontSize={11.6} />
                <IconImage src={coinsIcon} />
                <FlexChild />
                <Text size={18} type="Medium" color={FONT_LIGHT_GRAY}>
                  {thaiNewsDate}
                </Text>
              </UpperContent>
              <div dangerouslySetInnerHTML={{ __html: digitalNews.content }} />
              {radioContent}
            </NewsContent>
          </NewsLayout>
          <CommentLayout>
            <Comment userId={user?.userId!!} digitalNewsId={digitalNews.id} />
          </CommentLayout>
        </BodyLayout>
      )}

      <FooterContent
        style={{
          height: 80,
          display: 'flex',
          justifyContent: isMobile ? 'center' : 'flex-end',
          alignItems: 'center',
        }}
      >
        {digitalNews?.type === 'REGISTER' ? (
          <ReadButton onClick={() => setModalJoin(true)} disabled={!canAction}>
            {buttonText}
          </ReadButton>
        ) : (
          <ReadButton onClick={onActionClick} disabled={!canAction}>
            {buttonText}
          </ReadButton>
        )}
      </FooterContent>

      <ResponseModal visible={modalVisible} onCancel={onModalOk} hideFooter>
        <div>
          <CoinSuccessImagePanel>
            <Image src={responseModalData.image} />
          </CoinSuccessImagePanel>
          <CenteredText size={30} type="Bold">
            {digitalNews?.type === 'REGISTER' ? 'คุณได้เข้าร่วมแล้ว' : responseModalData.text}
          </CenteredText>
          <ModalButtonPanel>
            <ModalButton onClick={onModalOk}>
              <Text size={18} color="white">
                {responseModalData.buttonText}
              </Text>
            </ModalButton>
          </ModalButtonPanel>
        </div>
      </ResponseModal>

      <Modal visible={modalJoin} onCancel={() => setModalJoin(false)} hideFooter>
        <ModalContainer>
          <Text> {digitalNews?.title}</Text>
          <div style={{ display: 'flex', margin: '50px 0 0 0' }}>
            <Button
              onClick={() => setModalJoin(false)}
              style={{
                background: 'transparent',
                marginRight: 8,
                color: PRIMARY,
                borderColor: PRIMARY,
              }}
            >
              <Text size={20} type="SemiBold" color={FONT_BLACK}>
                ยกเลิก
              </Text>
            </Button>
            <Button onClick={onActionClick}>เข้าร่วม</Button>
          </div>
        </ModalContainer>
      </Modal>

      <Modal visible={modalVisibleFaild} onCancel={() => setModalVisibleFaild(false)} hideFooter>
        <ModalContainer>
          <Text size={30} type="Bold">
            ไม่สามารถโหวตได้
          </Text>
          <Text>กรุณาเลือก Option สำหรับโหวต</Text>
          <ModalButton onClick={() => setModalVisibleFaild(false)}>ปิด</ModalButton>
        </ModalContainer>
      </Modal>
    </Loading>
  )
}

export default NewsDetail
